import { useMemo } from "react";
import {
	buildDailyPLData,
	buildDirectionWinRateData,
	buildEquityCurveData,
	buildOutcomeData,
	buildPLDistributionData,
	buildPairPerformanceData,
	buildTpHitRateData,
	buildWinLossData,
} from "./chart-data";
import { DailyPLChart } from "./DailyPLChart";
import { DirectionWinRateChart } from "./DirectionWinRateChart";
import { EquityCurveChart } from "./EquityCurveChart";
import { OutcomeDistributionChart } from "./OutcomeDistributionChart";
import { PLDistributionChart } from "./PLDistributionChart";
import { PairPerformanceChart } from "./PairPerformanceChart";
import { TpHitRateChart } from "./TpHitRateChart";
import { WinLossBreakdownChart } from "./WinLossBreakdownChart";

interface TradeCaseInput {
	id: number;
	pair: string | null;
	direction: string | null;
	currentState: string;
	outcome: string | null;
	openedAt: string | null;
	closedAt: string | null;
	entryPriceFilled: string | null;
	profitLossMoney: string | null;
	profitLossPercent: string | null;
	tpHitsCount: number;
	tpTargetCount: number;
}

interface Props {
	tradeCases: TradeCaseInput[];
	isResizeFrozen?: boolean;
}

const ChartCard = ({
	title,
	children,
	tall,
}: {
	title: string;
	children: React.ReactNode;
	tall?: boolean;
}) => (
	<div
		className={`rounded-lg border tg-divider-border ${tall ? "col-span-full" : ""}`}
		style={{ background: "var(--color-tg-surface)" }}
	>
		<p className="px-3 pt-2.5 text-[0.68rem] font-semibold uppercase tracking-[0.08em] tg-text-muted">
			{title}
		</p>
		<div className={`px-1 pb-1 ${tall ? "h-[220px]" : "h-[180px]"}`}>
			{children}
		</div>
	</div>
);

export const TradeAuditCharts = ({ tradeCases, isResizeFrozen }: Props) => {
	const equityCurve = useMemo(
		() => buildEquityCurveData(tradeCases),
		[tradeCases],
	);
	const winLoss = useMemo(() => buildWinLossData(tradeCases), [tradeCases]);
	const outcomes = useMemo(() => buildOutcomeData(tradeCases), [tradeCases]);
	const tpHitRates = useMemo(
		() => buildTpHitRateData(tradeCases),
		[tradeCases],
	);
	const pairPerformance = useMemo(
		() => buildPairPerformanceData(tradeCases),
		[tradeCases],
	);
	const plDistribution = useMemo(
		() => buildPLDistributionData(tradeCases),
		[tradeCases],
	);
	const directionWinRate = useMemo(
		() => buildDirectionWinRateData(tradeCases),
		[tradeCases],
	);
	const dailyPL = useMemo(
		() => buildDailyPLData(tradeCases),
		[tradeCases],
	);

	return (
		<div className="tg-scrollbar h-full overflow-y-auto px-3 py-3">
			<div
				className="grid grid-cols-2 gap-3"
				style={isResizeFrozen ? { contain: "size layout paint" } : undefined}
			>
				<ChartCard title="Equity Curve" tall>
					<EquityCurveChart data={equityCurve} />
				</ChartCard>
				<ChartCard title="Win / Loss">
					<WinLossBreakdownChart data={winLoss} />
				</ChartCard>
				<ChartCard title="Outcome Distribution">
					<OutcomeDistributionChart data={outcomes} />
				</ChartCard>
				<ChartCard title="TP Hit Rates">
					<TpHitRateChart data={tpHitRates} />
				</ChartCard>
				<ChartCard title="Profit by Pair">
					<PairPerformanceChart data={pairPerformance} />
				</ChartCard>
				<ChartCard title="P/L Distribution">
					<PLDistributionChart data={plDistribution} />
				</ChartCard>
				<ChartCard title="Win Rate by Direction">
					<DirectionWinRateChart data={directionWinRate} />
				</ChartCard>
				<ChartCard title="Daily P/L" tall>
					<DailyPLChart data={dailyPL} />
				</ChartCard>
			</div>
		</div>
	);
};
